import { IFormatNumber } from './models/formart-number.model';

const benchmarkPercentCalcs = {
  cdi: (assetValue: number, indexValue: number): string =>
    ((assetValue / indexValue) * 100).toFixed(0),
  ibov: (assetValue: number, indexValue: number): string =>
    (assetValue - indexValue).toFixed(2),
};

export const percentIndicator = (
  assetStats: number,
  benchmarkStats: number,
  benchmark: string
) => {
  if (!benchmarkStats || assetStats === undefined || !benchmark) {
    return '-';
  }

  return Number(benchmarkPercentCalcs[benchmark](assetStats, benchmarkStats));
};

export const formatCnpj = (value: number | string): string => {
  if (!value) {
    return '';
  }

  const cnpj = ('000000000' + value).substr(-14);

  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatDecimalPlaces = (value: number, precision = 2) => {
  return value.toLocaleString('PT-br', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });
};

const formatThousandMask = (value: number, precision = 2): string => {
  const absoluteNumber = Math.abs(value);

  if (absoluteNumber >= 1e3 && absoluteNumber < 1e6)
    return `${formatDecimalPlaces(value / 1e3, precision)} mil`;

  if (absoluteNumber >= 1e6 && absoluteNumber < 1e9)
    return `${formatDecimalPlaces(value / 1e6, precision)} mi`;

  if (absoluteNumber >= 1e9 && absoluteNumber < 1e12)
    return `${formatDecimalPlaces(value / 1e9, precision)} bi`;

  if (absoluteNumber >= 1e12)
    return `${formatDecimalPlaces(value / 1e12, precision)} tri`;

  return `${formatDecimalPlaces(value, precision)}`;
};

export const formatNumber = (
  value: number | string,
  { type, thousandMask, precision = 2 }: IFormatNumber = {}
) => {
  const nullPlaceholder = '-';

  if (value === null || value === undefined || isNaN(Number(value))) {
    return nullPlaceholder;
  }

  const numberValue = Number(value);
  const formattedValue = formatDecimalPlaces(numberValue, precision);

  if (type && type === 'percent') {
    return `${formattedValue}%`;
  }

  if (type && type === 'money') {
    const valueWithThousand = thousandMask
      ? formatThousandMask(numberValue, precision)
      : formattedValue;
    return `R$ ${valueWithThousand}`;
  }

  if (thousandMask) {
    return formatThousandMask(numberValue, precision);
  }

  return formattedValue;
};

export const truncateText = (text: string, truncate: number = 60) => {
  if (!text) return text;

  return truncate && text.length > truncate
    ? `${text.slice(0, truncate)}...`
    : text;
};

export const convertInPercentage = (value: number): number => {
  return value * 100;
};

export const capitalyzeText = (value: string): string => {
  if (!value) return value;

  const numberVerify = /^[a-zA-Z]/;
  const isFirstLetterText = numberVerify.test(value);

  if (isFirstLetterText) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  return value;
};
